import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { loadStripe } from '@stripe/stripe-js';
import toast from 'react-hot-toast';
import Navbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import { useCart } from '../../context/CartContext';
import api from '../../utils/api';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_placeholder');

// ─────────────────────────────────────────────────────────────────
// CART PAGE
// ─────────────────────────────────────────────────────────────────
export function CartPage() {
  const { cart, updateQty, removeFromCart, total } = useCart();
  const shipping = total >= 500 ? 0 : 25;

  return (
    <>
      <Helmet><title>Your Cart | Sudath Gems</title></Helmet>
      <Navbar />
      <div style={{ paddingTop: 72, minHeight: '80vh' }}>
        <div style={{ background:'var(--surface)', borderBottom:'1px solid var(--border)', padding:'50px 0 36px' }}>
          <div className="container">
            <span style={{ fontSize:11, letterSpacing:3, textTransform:'uppercase', color:'var(--gold)', display:'block', marginBottom:10 }}>Your Selection</span>
            <h1 style={{ fontFamily:'var(--font-serif)', fontSize:'clamp(1.8rem,4vw,3rem)', fontWeight:300 }}>Shopping Cart</h1>
          </div>
        </div>

        <div className="container" style={{ padding:'60px 40px' }}>
          {cart.length === 0 ? (
            <div style={{ textAlign:'center', padding:'80px 20px' }}>
              <p style={{ fontFamily:'var(--font-serif)', fontSize:28, color:'var(--muted)', marginBottom:12 }}>Your cart is empty</p>
              <p style={{ fontSize:14, color:'var(--muted2)', marginBottom:32 }}>Discover our collection of certified natural gemstones</p>
              <Link to="/shop" className="btn btn-gold btn-lg">Browse Collection</Link>
            </div>
          ) : (
            <div className="cart-grid" style={{ display:'grid', gridTemplateColumns:'1fr 360px', gap:48, alignItems:'start' }}>
              {/* Cart items */}
              <div>
                {cart.map(item => (
                  <div key={item.gemId} style={{ display:'flex', gap:24, padding:'28px 0', borderBottom:'1px solid var(--border)', alignItems:'center' }}>
                    <img src={item.image || 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=300&q=70'} alt={item.name}
                      style={{ width:100, height:100, objectFit:'cover', borderRadius:4, border:'1px solid var(--border)' }} />
                    <div style={{ flex:1 }}>
                      <Link to={`/gems/${item.slug}`} style={{ fontFamily:'var(--font-serif)', fontSize:20, color:'var(--white)', display:'block', marginBottom:6 }}>{item.name}</Link>
                      <p style={{ fontSize:13, color:'var(--gold)', marginBottom:12 }}>${Number(item.price).toLocaleString()} USD each</p>
                      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        <div style={{ display:'flex', alignItems:'center', border:'1px solid var(--border2)', borderRadius:4, overflow:'hidden' }}>
                          <button onClick={() => updateQty(item.gemId, item.quantity - 1)} style={{ padding:'6px 14px', color:'var(--white)', fontSize:18, borderRight:'1px solid var(--border2)' }}>−</button>
                          <span style={{ padding:'6px 16px', fontSize:14, color:'var(--white)', minWidth:40, textAlign:'center' }}>{item.quantity}</span>
                          <button onClick={() => updateQty(item.gemId, Math.min(item.quantity + 1, item.stock))} style={{ padding:'6px 14px', color:'var(--white)', fontSize:18, borderLeft:'1px solid var(--border2)' }}>+</button>
                        </div>
                        <button onClick={() => removeFromCart(item.gemId)} style={{ fontSize:12, color:'var(--muted)', letterSpacing:1, textTransform:'uppercase', padding:'6px 12px', border:'1px solid var(--border)', borderRadius:4, transition:'var(--transition)' }}
                          onMouseEnter={e=>{e.target.style.borderColor='var(--danger)';e.target.style.color='var(--danger)'}}
                          onMouseLeave={e=>{e.target.style.borderColor='var(--border)';e.target.style.color='var(--muted)'}}>
                          Remove
                        </button>
                      </div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <p style={{ fontFamily:'var(--font-serif)', fontSize:24, color:'var(--gold)' }}>${(item.price * item.quantity).toLocaleString()}</p>
                      <p style={{ fontSize:11, color:'var(--muted)' }}>USD</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:28, position:'sticky', top:100 }}>
                <h3 style={{ fontFamily:'var(--font-serif)', fontSize:22, fontWeight:400, marginBottom:24 }}>Order Summary</h3>
                <div style={{ display:'flex', flexDirection:'column', gap:14, marginBottom:24, paddingBottom:24, borderBottom:'1px solid var(--border)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span style={{ fontSize:13, color:'var(--muted)' }}>Subtotal</span>
                    <span style={{ fontSize:13, color:'var(--white)' }}>${total.toLocaleString()} USD</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span style={{ fontSize:13, color:'var(--muted)' }}>Shipping</span>
                    <span style={{ fontSize:13, color: shipping===0 ? 'var(--success)' : 'var(--white)' }}>{shipping===0 ? 'FREE' : `$${shipping} USD`}</span>
                  </div>
                  {shipping > 0 && <p style={{ fontSize:11, color:'var(--gold)', opacity:0.7 }}>Free shipping on orders over $500</p>}
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:28 }}>
                  <span style={{ fontFamily:'var(--font-serif)', fontSize:18 }}>Total</span>
                  <div style={{ textAlign:'right' }}>
                    <span style={{ fontFamily:'var(--font-serif)', fontSize:24, color:'var(--gold)' }}>${(total + shipping).toLocaleString()}</span>
                    <span style={{ fontSize:11, color:'var(--muted)', display:'block' }}>USD</span>
                  </div>
                </div>
                <Link to="/checkout" className="btn btn-gold btn-lg" style={{ width:'100%', justifyContent:'center', display:'flex' }}>
                  Proceed to Checkout
                </Link>
                <p style={{ fontSize:11, color:'var(--muted2)', textAlign:'center', marginTop:16, lineHeight:1.6 }}>
                  🔒 Secure checkout via Stripe.<br/>All prices in USD.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// CHECKOUT PAGE
// ─────────────────────────────────────────────────────────────────
export function CheckoutPage() {
  const { cart, total, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [shipping, setShipping] = useState('standard');
  const [form, setForm] = useState({ name:'', email:'', phone:'', address:'', city:'', country:'', postal:'' });

  const shippingCost = total >= 500 ? 0 : (shipping === 'express' ? 55 : 25);
  const grandTotal   = total + shippingCost;

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!cart.length) return toast.error('Your cart is empty');
    setLoading(true);
    try {
      const { data } = await api.post('/payments/create-checkout', {
        items: cart.map(i => ({ gemId: i.gemId, name: i.name, quantity: i.quantity })),
        customerInfo: form,
        shippingMethod: total >= 500 ? 'free' : shipping,
      });
      if (data.url) window.location.href = data.url;
      else toast.error('Could not create checkout session');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Checkout failed. Please try again.');
    } finally { setLoading(false); }
  };

  const f = (k) => (e) => setForm({...form, [k]: e.target.value});

  return (
    <>
      <Helmet><title>Checkout | Sudath Gems</title></Helmet>
      <Navbar />
      <div style={{ paddingTop: 72, minHeight: '80vh' }}>
        <div style={{ background:'var(--surface)', borderBottom:'1px solid var(--border)', padding:'50px 0 36px' }}>
          <div className="container">
            <span style={{ fontSize:11, letterSpacing:3, textTransform:'uppercase', color:'var(--gold)', display:'block', marginBottom:10 }}>Secure Checkout</span>
            <h1 style={{ fontFamily:'var(--font-serif)', fontSize:'clamp(1.8rem,4vw,3rem)', fontWeight:300 }}>Complete Your Order</h1>
          </div>
        </div>

        <div className="container" style={{ padding:'60px 40px' }}>
          <form onSubmit={handleCheckout}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 380px', gap:48, alignItems:'start' }}>

              {/* Form */}
              <div>
                <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:32, marginBottom:24 }}>
                  <h3 style={{ fontFamily:'var(--font-serif)', fontSize:20, fontWeight:400, marginBottom:24 }}>Contact Information</h3>
                  <div className="grid-2">
                    <div className="form-group">
                      <label className="form-label">Full Name *</label>
                      <input required value={form.name} onChange={f('name')} className="form-input" placeholder="John Smith" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email *</label>
                      <input required type="email" value={form.email} onChange={f('email')} className="form-input" placeholder="john@example.com" />
                    </div>
                    <div className="form-group" style={{ gridColumn:'span 2' }}>
                      <label className="form-label">Phone / WhatsApp</label>
                      <input value={form.phone} onChange={f('phone')} className="form-input" placeholder="+1 234 567 8900" />
                    </div>
                  </div>
                </div>

                <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:32, marginBottom:24 }}>
                  <h3 style={{ fontFamily:'var(--font-serif)', fontSize:20, fontWeight:400, marginBottom:24 }}>Shipping Address</h3>
                  <div className="form-group">
                    <label className="form-label">Street Address *</label>
                    <input required value={form.address} onChange={f('address')} className="form-input" placeholder="123 Main Street, Apt 4B" />
                  </div>
                  <div className="grid-2">
                    <div className="form-group">
                      <label className="form-label">City *</label>
                      <input required value={form.city} onChange={f('city')} className="form-input" placeholder="New York" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Country *</label>
                      <input required value={form.country} onChange={f('country')} className="form-input" placeholder="United States" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Postal Code</label>
                      <input value={form.postal} onChange={f('postal')} className="form-input" placeholder="10001" />
                    </div>
                  </div>
                </div>

                {total < 500 && (
                  <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:32 }}>
                    <h3 style={{ fontFamily:'var(--font-serif)', fontSize:20, fontWeight:400, marginBottom:24 }}>Shipping Method</h3>
                    {[['standard','Standard International',25,'7–14 business days'],['express','Express International',55,'3–5 business days']].map(([val,label,cost,time])=>(
                      <label key={val} style={{ display:'flex', alignItems:'center', gap:16, padding:16, border:`1px solid ${shipping===val?'var(--gold)':'var(--border)'}`, borderRadius:4, cursor:'pointer', marginBottom:10, transition:'var(--transition)', background: shipping===val?'rgba(201,169,110,0.05)':'transparent' }}>
                        <input type="radio" value={val} checked={shipping===val} onChange={()=>setShipping(val)} style={{ accentColor:'var(--gold)' }} />
                        <div style={{ flex:1 }}>
                          <p style={{ fontSize:13, color:'var(--white)', fontWeight:500 }}>{label}</p>
                          <p style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>{time}</p>
                        </div>
                        <span style={{ fontSize:14, color:'var(--gold)' }}>${cost}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Summary */}
              <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:28, position:'sticky', top:100 }}>
                <h3 style={{ fontFamily:'var(--font-serif)', fontSize:20, fontWeight:400, marginBottom:20 }}>Order Summary</h3>
                <div style={{ maxHeight:240, overflowY:'auto', marginBottom:20 }}>
                  {cart.map(item => (
                    <div key={item.gemId} style={{ display:'flex', gap:12, alignItems:'center', padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
                      <img src={item.image} alt="" style={{ width:48, height:48, objectFit:'cover', borderRadius:3 }} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <p style={{ fontSize:13, color:'var(--white)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{item.name}</p>
                        <p style={{ fontSize:11, color:'var(--muted)' }}>×{item.quantity}</p>
                      </div>
                      <span style={{ fontSize:13, color:'var(--gold)' }}>${(item.price*item.quantity).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:12, paddingBottom:16, borderBottom:'1px solid var(--border)', marginBottom:16 }}>
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span style={{ fontSize:13, color:'var(--muted)' }}>Subtotal</span>
                    <span style={{ fontSize:13 }}>${total.toLocaleString()} USD</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span style={{ fontSize:13, color:'var(--muted)' }}>Shipping</span>
                    <span style={{ fontSize:13, color: shippingCost===0?'var(--success)':'var(--white)' }}>{shippingCost===0?'FREE':`$${shippingCost}`}</span>
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:28 }}>
                  <span style={{ fontFamily:'var(--font-serif)', fontSize:18 }}>Total</span>
                  <div style={{ textAlign:'right' }}>
                    <p style={{ fontFamily:'var(--font-serif)', fontSize:26, color:'var(--gold)' }}>${grandTotal.toLocaleString()}</p>
                    <p style={{ fontSize:11, color:'var(--muted)' }}>USD</p>
                  </div>
                </div>
                <button type="submit" disabled={loading} className="btn btn-gold btn-lg" style={{ width:'100%', justifyContent:'center', display:'flex' }}>
                  {loading ? 'Redirecting to Stripe...' : `Pay $${grandTotal.toLocaleString()} USD`}
                </button>
                <p style={{ fontSize:11, color:'var(--muted2)', textAlign:'center', marginTop:14, lineHeight:1.6 }}>
                  🔒 Powered by Stripe. Your payment info is never stored on our servers.
                </p>
              </div>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default CartPage;
